import * as React from "react";
import { Award } from "lucide-react";
import {
  formatLoyaltyMoney,
  loyaltyRedeemState,
  type LoyaltySummary,
} from "@baluu/ui/lib/checkout/loyalty";

export type LoyaltyCheckoutSectionProps = {
  summary: LoyaltySummary;
  /** Currency SYMBOL ("£"), not the ISO code. */
  currency?: string;
  redeemPoints: boolean;
  onToggleRedeem: (next: boolean) => void;
  /** The basket holds a deposit item: the server applies no loyalty perk or
   * points to it (400 LOYALTY_POINTS_DEPOSIT_UNSUPPORTED on points), so the
   * block says so instead of offering them. */
  blockedByDeposit?: boolean;
};

/**
 * Checkout block: the customer's tier perk, and the opt-in to spend points on
 * this order. The toggle only sets a flag — the server recomputes every
 * figure, so the charge can never drift from what the block promises. Shared
 * by the widget's basket checkout and the webcomponents overlay; each fetches
 * the summary its own way and renders nothing until it has one.
 *
 * A native checkbox on purpose: no portal, so it works inside a shadow root.
 */
export function LoyaltyCheckoutSection({
  summary,
  currency = "£",
  redeemPoints,
  onToggleRedeem,
  blockedByDeposit = false,
}: LoyaltyCheckoutSectionProps) {
  if (blockedByDeposit) {
    return (
      <div className="space-y-2 py-1">
        <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
          <Award className="w-3.5 h-3.5" />
          Loyalty
        </p>
        <p className="text-xs text-muted-foreground">
          Points and member discounts can&apos;t be used on a booking paid by deposit.
        </p>
      </div>
    );
  }

  const redeem = loyaltyRedeemState(summary);
  const perk = summary.tier?.perkPercentOff ?? null;

  return (
    <div className="space-y-2 py-1">
      <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
        <Award className="w-3.5 h-3.5" />
        Loyalty
      </p>

      {summary.tier && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2.5 py-0.5">
            {summary.tier.name}
          </span>
          {perk ? (
            <span className="text-xs text-muted-foreground">
              {perk}% member discount applied automatically
            </span>
          ) : null}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        You have {summary.pointsBalance.toLocaleString()} points
        {summary.pointsValuePence > 0
          ? ` (worth ${formatLoyaltyMoney(summary.pointsValuePence, currency)})`
          : ""}
        .
      </p>

      {redeem.kind === "redeem" ? (
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-input"
            checked={redeemPoints}
            onChange={(e) => onToggleRedeem(e.target.checked)}
          />
          <span>
            Use my points (&minus;
            {formatLoyaltyMoney(redeem.maxValuePence, currency)} max)
          </span>
        </label>
      ) : redeem.kind === "earn-more" ? (
        <p className="text-xs text-muted-foreground">
          Earn {redeem.pointsNeeded} more points to start redeeming.
        </p>
      ) : null}
    </div>
  );
}
